import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    TextField,
    Typography,
    useTheme,
    Switch,
    FormControlLabel,
    Checkbox,
    InputAdornment,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import GetAppIcon from '@material-ui/icons/GetApp'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import EventIcon from '@material-ui/icons/Event'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import CategoryIcon from '@material-ui/icons/Category'
import SecurityIcon from '@material-ui/icons/Security'
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar'
import BeachAccessIcon from '@material-ui/icons/BeachAccess'
import BuildIcon from '@material-ui/icons/Build'
import EditIcon from '@material-ui/icons/Edit'
import CheckIcon from '@material-ui/icons/Check'
import ClearIcon from '@material-ui/icons/Clear'
import BusinessIcon from '@material-ui/icons/Business'
import NotesIcon from '@material-ui/icons/Notes'
import { Container } from '../../components'
import { SampleCOI, UpdateSampleCOIRequest } from '../../hooks/useSampleCOIs'
import { Service } from '../../models/Services/types'
import { getServiceImageDetails } from '../../models/WorkOrder/services'

interface SampleCOIViewModalProps {
    isOpen: boolean
    onClose: () => void
    sampleCOI: SampleCOI | null
    onUpdate?: (id: number, data: UpdateSampleCOIRequest) => void
    isUpdating?: boolean
    allServices?: Service[]
}

type EditableField =
    | 'certificate_holder_name'
    | 'certificate_holder_address'
    | 'description_of_operations'
    | 'gl_each_occurrence'
    | 'gl_damage_to_rented_premises'
    | 'gl_med_exp'
    | 'gl_personal_adv_injury'
    | 'gl_general_aggregate'
    | 'gl_products_comp_op_agg'
    | 'auto_combined_single_limit'
    | 'auto_bodily_injury_per_person'
    | 'auto_bodily_injury_per_accident'
    | 'auto_property_damage'
    | 'umbrella_each_occurrence'
    | 'umbrella_aggregate'
    | 'wc_statutory_limits'
    | 'wc_el_each_accident'
    | 'wc_el_disease_ea_employee'
    | 'wc_el_disease_policy_limit'
    | 'services'

export const SampleCOIViewModal: React.FC<SampleCOIViewModalProps> = ({
    isOpen,
    onClose,
    sampleCOI,
    onUpdate,
    isUpdating,
    allServices = [],
}) => {
    const theme = useTheme()
    const [editingField, setEditingField] = useState<EditableField | null>(null)
    const [editValue, setEditValue] = useState<string>('')
    const [editBoolValue, setEditBoolValue] = useState<boolean>(false)
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([])
    const [failedIcons, setFailedIcons] = useState<Set<number>>(new Set())

    const handleIconError = useCallback((serviceId: number) => {
        setFailedIcons((prev) => new Set(prev).add(serviceId))
    }, [])

    // Reset editing state when modal closes or COI changes
    useEffect(() => {
        if (!isOpen) {
            setEditingField(null)
            setEditValue('')
            setEditBoolValue(false)
            setFailedIcons(new Set())
        }
    }, [isOpen, sampleCOI?.id])

    // Memoize formatters to prevent recreation on each render
    const formatDate = useCallback((dateString: string) => {
        const [year, month, day] = dateString.split('T')[0].split('-')
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ]
        return `${months[parseInt(month, 10) - 1]} ${parseInt(
            day,
            10,
        )}, ${year}`
    }, [])

    const formatCurrency = useCallback((value: string | null) => {
        if (!value) return '—'
        const num = parseFloat(value)
        if (isNaN(num)) return value
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num)
    }, [])

    const parseCurrencyToNumber = useCallback((value: string): string => {
        // Remove currency symbols and commas
        const cleaned = value.replace(/[$,]/g, '').trim()
        const num = parseFloat(cleaned)
        if (isNaN(num)) return ''
        return num.toFixed(2)
    }, [])

    // Memoize computed values
    const isPdf = useMemo(
        () => sampleCOI?.name.toLowerCase().endsWith('.pdf') ?? false,
        [sampleCOI?.name],
    )

    const viewerUrl = useMemo(() => {
        if (!sampleCOI?.file) return ''
        if (isPdf) {
            return sampleCOI.file
        }
        return `https://docs.google.com/gview?url=${encodeURIComponent(
            sampleCOI.file,
        )}&embedded=true`
    }, [sampleCOI?.file, isPdf])

    // Early return after hooks
    if (!sampleCOI) return null

    const handleDownload = () => {
        if (sampleCOI.file) {
            const link = window.document.createElement('a')
            link.href = sampleCOI.file
            link.download = sampleCOI.name
            link.click()
        }
    }

    const handleOpenInNewTab = () => {
        if (sampleCOI.file) {
            window.open(sampleCOI.file, '_blank')
        }
    }

    const startEditing = (field: EditableField) => {
        if (!onUpdate) return
        setEditingField(field)

        if (field === 'wc_statutory_limits') {
            setEditBoolValue(sampleCOI.wc_statutory_limits || false)
        } else if (field === 'services') {
            setSelectedServiceIds(sampleCOI.services.map((s) => s.id))
        } else {
            const value = sampleCOI[field]
            setEditValue(value || '')
        }
    }

    const cancelEditing = () => {
        setEditingField(null)
        setEditValue('')
        setEditBoolValue(false)
        setSelectedServiceIds([])
    }

    const saveEdit = () => {
        if (!onUpdate || !editingField) return

        const updateData: UpdateSampleCOIRequest = {}

        if (editingField === 'wc_statutory_limits') {
            updateData.wc_statutory_limits = editBoolValue
        } else if (editingField === 'services') {
            updateData.service_ids = selectedServiceIds
        } else if (
            editingField.startsWith('gl_') ||
            editingField.startsWith('auto_') ||
            editingField.startsWith('umbrella_') ||
            editingField.startsWith('wc_el_')
        ) {
            // Currency fields
            const numValue = parseCurrencyToNumber(editValue)
            Object.assign(updateData, { [editingField]: numValue || null })
        } else {
            // Text fields
            Object.assign(updateData, { [editingField]: editValue || null })
        }

        onUpdate(sampleCOI.id, updateData)
        cancelEditing()
    }

    const toggleService = (serviceId: number) => {
        setSelectedServiceIds((prev) =>
            prev.includes(serviceId)
                ? prev.filter((id) => id !== serviceId)
                : [...prev, serviceId],
        )
    }

    // Render editable field with edit icon
    const renderEditableTextField = (
        label: string,
        field: EditableField,
        value: string | null,
        isCurrency: boolean = false,
    ) => {
        const isEditing = editingField === field
        const displayValue = isCurrency ? formatCurrency(value) : value || '—'

        return (
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
            >
                <Typography variant="caption" color="textSecondary">
                    {label}
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    {isEditing ? (
                        <>
                            <TextField
                                size="small"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                variant="outlined"
                                style={{ width: 120 }}
                                InputProps={{
                                    style: { fontSize: 12, padding: '2px 8px' },
                                    startAdornment: isCurrency ? (
                                        <InputAdornment position="start">
                                            $
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEdit()
                                    if (e.key === 'Escape') cancelEditing()
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={saveEdit}
                                disabled={isUpdating}
                            >
                                <CheckIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.success.main,
                                    }}
                                />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEditing}>
                                <ClearIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.error.main,
                                    }}
                                />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="caption"
                                style={{ fontWeight: 500 }}
                            >
                                {displayValue}
                            </Typography>
                            {onUpdate && (
                                <IconButton
                                    size="small"
                                    onClick={() => startEditing(field)}
                                    style={{ padding: 2, marginLeft: 4 }}
                                >
                                    <EditIcon
                                        style={{
                                            fontSize: 14,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </IconButton>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        )
    }

    // Render boolean field (for statutory limits)
    const renderBooleanField = (
        label: string,
        field: EditableField,
        value: boolean | null,
    ) => {
        const isEditing = editingField === field

        return (
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
            >
                <Typography variant="caption" color="textSecondary">
                    {label}
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    {isEditing ? (
                        <>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editBoolValue}
                                        onChange={(e) =>
                                            setEditBoolValue(e.target.checked)
                                        }
                                        size="small"
                                        color="primary"
                                    />
                                }
                                label={editBoolValue ? 'Yes' : 'No'}
                                style={{ margin: 0 }}
                            />
                            <IconButton
                                size="small"
                                onClick={saveEdit}
                                disabled={isUpdating}
                            >
                                <CheckIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.success.main,
                                    }}
                                />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEditing}>
                                <ClearIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.error.main,
                                    }}
                                />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="caption"
                                style={{ fontWeight: 500 }}
                            >
                                {value ? 'Yes' : '—'}
                            </Typography>
                            {onUpdate && (
                                <IconButton
                                    size="small"
                                    onClick={() => startEditing(field)}
                                    style={{ padding: 2, marginLeft: 4 }}
                                >
                                    <EditIcon
                                        style={{
                                            fontSize: 14,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </IconButton>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        )
    }

    // Render large text field (for description, etc.)
    const renderEditableTextArea = (
        label: string,
        icon: React.ReactNode,
        field: EditableField,
        value: string | null,
    ) => {
        const isEditing = editingField === field

        return (
            <Box mb={3}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={0.5}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        {icon}
                        <Typography variant="body2" color="textSecondary">
                            {label}
                        </Typography>
                    </Box>
                    {!isEditing && onUpdate && (
                        <IconButton
                            size="small"
                            onClick={() => startEditing(field)}
                            style={{ padding: 4 }}
                        >
                            <EditIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                        </IconButton>
                    )}
                </Box>
                {isEditing ? (
                    <Box style={{ marginLeft: 26 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            variant="outlined"
                            autoFocus
                        />
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            mt={1}
                            style={{ gap: 4 }}
                        >
                            <Button
                                size="small"
                                onClick={cancelEditing}
                                style={{ minWidth: 0, padding: '2px 8px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={saveEdit}
                                disabled={isUpdating}
                                style={{ minWidth: 0, padding: '2px 8px' }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Typography
                        variant="body2"
                        style={{
                            marginLeft: 26,
                            fontStyle: value ? 'normal' : 'italic',
                            color: value
                                ? theme.palette.text.primary
                                : theme.palette.text.secondary,
                        }}
                    >
                        {value || 'Not specified'}
                    </Typography>
                )}
            </Box>
        )
    }

    // Get service icon/color - uses icon (not src image)
    const getServiceVisual = (service: Service) => {
        const serviceDetails = getServiceImageDetails(
            service.id,
            service.service_category,
        )
        if (serviceDetails?.icon && !failedIcons.has(service.id)) {
            return (
                <img
                    src={serviceDetails.icon}
                    alt={service.name}
                    style={{
                        width: 20,
                        height: 20,
                        objectFit: 'contain',
                    }}
                    onError={() => handleIconError(service.id)}
                />
            )
        }
        return (
            <Box
                style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: service.color || '#9e9e9e',
                }}
            />
        )
    }

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                style: {
                    height: '90vh',
                    maxHeight: '90vh',
                    width: '95vw',
                    maxWidth: 1400,
                },
            }}
        >
            {/* Header */}
            <Container
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: 'row',
                    padding: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: theme.spacing(2) }}
                >
                    <Box
                        style={{
                            height: 40,
                            width: 40,
                            borderRadius: theme.spacing(1),
                            backgroundColor: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.primary.contrastText,
                        }}
                    >
                        <DescriptionIcon />
                    </Box>
                    <Typography variant="h6">{sampleCOI.name}</Typography>
                </Box>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Container>

            {/* Content */}
            <DialogContent
                style={{
                    padding: 0,
                    display: 'flex',
                    height: 'calc(90vh - 140px)',
                }}
            >
                {/* Left: Details */}
                <Box
                    style={{
                        width: 400,
                        minWidth: 400,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(3),
                        overflowY: 'auto',
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    {/* Date Info */}
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            display: 'block',
                        }}
                    >
                        Sample COI Details
                    </Typography>

                    <Box mb={2}>
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <EventIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Created
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginLeft: 26 }}
                        >
                            {formatDate(sampleCOI.created_date)}
                        </Typography>
                    </Box>

                    <Box mb={2}>
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <AutorenewIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Last Modified
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginLeft: 26 }}
                        >
                            {formatDate(sampleCOI.modified_date)}
                        </Typography>
                    </Box>

                    {/* Services Section */}
                    <Box
                        mb={3}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(2),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1.5}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: theme.spacing(1) }}
                            >
                                <CategoryIcon
                                    style={{
                                        fontSize: 18,
                                        color: theme.palette.primary.main,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 600 }}
                                >
                                    Applicable Services
                                </Typography>
                                <Chip
                                    label={sampleCOI.services.length}
                                    size="small"
                                    style={{
                                        height: 20,
                                        fontSize: 11,
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        color: '#fff',
                                    }}
                                />
                            </Box>
                            {onUpdate && editingField !== 'services' && (
                                <IconButton
                                    size="small"
                                    onClick={() => startEditing('services')}
                                    style={{ padding: 4 }}
                                >
                                    <EditIcon
                                        style={{
                                            fontSize: 16,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </IconButton>
                            )}
                        </Box>

                        {editingField === 'services' ? (
                            <Box>
                                <Box
                                    style={{
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        backgroundColor: '#fff',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: theme.spacing(0.5),
                                        padding: theme.spacing(1),
                                    }}
                                >
                                    {allServices.map((service) => (
                                        <Box
                                            key={service.id}
                                            display="flex"
                                            alignItems="center"
                                            style={{
                                                gap: 10,
                                                padding: '6px 4px',
                                                cursor: 'pointer',
                                                borderRadius: 4,
                                                backgroundColor: selectedServiceIds.includes(
                                                    service.id,
                                                )
                                                    ? theme.palette.primary
                                                          .light + '20'
                                                    : 'transparent',
                                            }}
                                            onClick={() =>
                                                toggleService(service.id)
                                            }
                                        >
                                            <Checkbox
                                                checked={selectedServiceIds.includes(
                                                    service.id,
                                                )}
                                                size="small"
                                                color="primary"
                                                style={{ padding: 0 }}
                                            />
                                            {getServiceVisual(service)}
                                            <Typography variant="body2">
                                                {service.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    mt={1.5}
                                    style={{ gap: 8 }}
                                >
                                    <Button
                                        size="small"
                                        onClick={cancelEditing}
                                        variant="outlined"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={saveEdit}
                                        disabled={isUpdating}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                {sampleCOI.services.length > 0 ? (
                                    <Box
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: theme.spacing(0.75),
                                            maxHeight: 150,
                                            overflowY: 'auto',
                                        }}
                                    >
                                        {sampleCOI.services.map((service) => (
                                            <Box
                                                key={service.id}
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: theme.spacing(1.5),
                                                    padding: theme.spacing(
                                                        0.75,
                                                        1,
                                                    ),
                                                    backgroundColor: '#fff',
                                                    borderRadius: theme.spacing(
                                                        0.5,
                                                    ),
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Box
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: 4,
                                                        backgroundColor:
                                                            service.color +
                                                            '20',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {getServiceVisual(service)}
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    style={{ fontWeight: 500 }}
                                                >
                                                    {service.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{
                                            fontStyle: 'italic',
                                            textAlign: 'center',
                                            padding: theme.spacing(1),
                                        }}
                                    >
                                        No services assigned
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>

                    {/* Certificate Holder */}
                    {renderEditableTextArea(
                        'Certificate Holder',
                        <BusinessIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'certificate_holder_name',
                        sampleCOI.certificate_holder_name,
                    )}

                    {renderEditableTextArea(
                        'Certificate Holder Address',
                        <BusinessIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'certificate_holder_address',
                        sampleCOI.certificate_holder_address,
                    )}

                    {renderEditableTextArea(
                        'Description of Operations',
                        <NotesIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'description_of_operations',
                        sampleCOI.description_of_operations,
                    )}

                    {/* Coverage Requirements */}
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            display: 'block',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        Coverage Requirements
                    </Typography>

                    {/* General Liability */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <SecurityIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.primary.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                General Liability
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderEditableTextField(
                                'Each Occurrence',
                                'gl_each_occurrence',
                                sampleCOI.gl_each_occurrence,
                                true,
                            )}
                            {renderEditableTextField(
                                'Damage to Rented Premises',
                                'gl_damage_to_rented_premises',
                                sampleCOI.gl_damage_to_rented_premises,
                                true,
                            )}
                            {renderEditableTextField(
                                'Medical Expense',
                                'gl_med_exp',
                                sampleCOI.gl_med_exp,
                                true,
                            )}
                            {renderEditableTextField(
                                'Personal & Adv. Injury',
                                'gl_personal_adv_injury',
                                sampleCOI.gl_personal_adv_injury,
                                true,
                            )}
                            {renderEditableTextField(
                                'General Aggregate',
                                'gl_general_aggregate',
                                sampleCOI.gl_general_aggregate,
                                true,
                            )}
                            {renderEditableTextField(
                                'Products/Comp Ops Agg',
                                'gl_products_comp_op_agg',
                                sampleCOI.gl_products_comp_op_agg,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* Auto Liability */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <DirectionsCarIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.info.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                Auto Liability
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderEditableTextField(
                                'Combined Single Limit',
                                'auto_combined_single_limit',
                                sampleCOI.auto_combined_single_limit,
                                true,
                            )}
                            {renderEditableTextField(
                                'Bodily Injury (Per Person)',
                                'auto_bodily_injury_per_person',
                                sampleCOI.auto_bodily_injury_per_person,
                                true,
                            )}
                            {renderEditableTextField(
                                'Bodily Injury (Per Accident)',
                                'auto_bodily_injury_per_accident',
                                sampleCOI.auto_bodily_injury_per_accident,
                                true,
                            )}
                            {renderEditableTextField(
                                'Property Damage',
                                'auto_property_damage',
                                sampleCOI.auto_property_damage,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* Umbrella */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <BeachAccessIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.warning.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                Umbrella / Excess Liability
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderEditableTextField(
                                'Each Occurrence',
                                'umbrella_each_occurrence',
                                sampleCOI.umbrella_each_occurrence,
                                true,
                            )}
                            {renderEditableTextField(
                                'Aggregate',
                                'umbrella_aggregate',
                                sampleCOI.umbrella_aggregate,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* Workers' Comp */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <BuildIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.error.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                Workers&apos; Compensation
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderBooleanField(
                                'Statutory Limits',
                                'wc_statutory_limits',
                                sampleCOI.wc_statutory_limits,
                            )}
                            {renderEditableTextField(
                                'E.L. Each Accident',
                                'wc_el_each_accident',
                                sampleCOI.wc_el_each_accident,
                                true,
                            )}
                            {renderEditableTextField(
                                'E.L. Disease (Each Employee)',
                                'wc_el_disease_ea_employee',
                                sampleCOI.wc_el_disease_ea_employee,
                                true,
                            )}
                            {renderEditableTextField(
                                'E.L. Disease (Policy Limit)',
                                'wc_el_disease_policy_limit',
                                sampleCOI.wc_el_disease_policy_limit,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* AI Summary */}
                    <Box
                        style={{
                            backgroundColor: theme.palette.info.light + '15',
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(2),
                            border: `1px solid ${theme.palette.info.light}`,
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{
                                gap: theme.spacing(1),
                                marginBottom: theme.spacing(1.5),
                            }}
                        >
                            <Box
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    backgroundColor: theme.palette.info.main,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <AutorenewIcon
                                    style={{ fontSize: 16, color: '#fff' }}
                                />
                            </Box>
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                AI Summary
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            style={{
                                lineHeight: 1.7,
                                fontStyle: sampleCOI.ai_summary
                                    ? 'normal'
                                    : 'italic',
                                color: sampleCOI.ai_summary
                                    ? theme.palette.text.primary
                                    : theme.palette.text.secondary,
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {sampleCOI.ai_summary ||
                                'No summary available yet. The document may still be processing.'}
                        </Typography>
                    </Box>
                </Box>

                {/* Right: Document Viewer */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                    {sampleCOI.file ? (
                        <iframe
                            src={viewerUrl}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                backgroundColor: theme.palette.background.paper,
                            }}
                            title={sampleCOI.name}
                        />
                    ) : (
                        <Box
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor={theme.palette.grey[100]}
                        >
                            <Typography color="textSecondary">
                                No document available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions
                style={{
                    padding: theme.spacing(2),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'space-between',
                }}
            >
                <Button
                    onClick={handleOpenInNewTab}
                    color="default"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                >
                    Open in New Tab
                </Button>
                <Box display="flex" style={{ gap: theme.spacing(1) }}>
                    <Button
                        onClick={onClose}
                        color="default"
                        variant="outlined"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleDownload}
                        color="primary"
                        variant="contained"
                        startIcon={<GetAppIcon />}
                    >
                        Download
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
